import React, { useState, useEffect, useRef } from 'react';
import { UrlEncoderService } from '../../lib/services/urlEncoderService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType, UrlSpaceHandling, UrlCaseHandling } from '../../lib/types';
import './UrlEncoder.css';

export const UrlEncoder: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [spaceHandling, setSpaceHandling] = useState<UrlSpaceHandling>(UrlSpaceHandling.Encode);
  const [caseHandling, setCaseHandling] = useState<UrlCaseHandling>(UrlCaseHandling.Preserve);
  const [partialEncoding, setPartialEncoding] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleConvert = async () => {
    if (!input.trim()) {
      setOutput('');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const processingKey = `${mode}-${spaceHandling}-${caseHandling}-${partialEncoding}-${input.substring(0, 50)}`;
    processingRef.current = processingKey;

    try {
      const config = UrlEncoderService.createConfiguration(
        input,
        mode === 'encode' ? 'encode' : 'decode',
        {
          spaceHandling,
          caseHandling,
          partialEncoding
        }
      );

      let result;
      if (mode === 'encode') {
        result = await UrlEncoderService.encode(config);
      } else {
        result = await UrlEncoderService.decode(config);
      }

      // Check if this is still the latest operation
      if (processingRef.current === processingKey) {
        setOutput(result.outputData as string);
        setError(null);
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, EncoderToolType.URL_ENCODER);
        setError(errorMessage);
        setOutput('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (input) {
      const timer = setTimeout(() => {
        handleConvert();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else {
      setOutput('');
      setError(null);
      setIsLoading(false);
    }
  }, [input, mode, spaceHandling, caseHandling, partialEncoding]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Result';
    }
  };

  const getButtonClass = () => {
    const baseClass = 'copy-button';
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  const handleBatchProcess = async () => {
    const lines = input.split('\n').filter(line => line.trim());
    if (lines.length <= 1) return;

    setIsLoading(true);
    setError(null);

    try {
      const config = UrlEncoderService.createConfiguration(
        input,
        mode === 'encode' ? 'encode' : 'decode',
        {
          spaceHandling,
          caseHandling,
          partialEncoding
        }
      );

      const result = mode === 'encode'
        ? await UrlEncoderService.encode(config)
        : await UrlEncoderService.decode(config);

      setOutput(result.outputData as string);
      setError(null);
    } catch (err) {
      const errorMessage = ErrorHandler.handle(err, EncoderToolType.URL_ENCODER);
      setError(errorMessage);
      setOutput('');
    } finally {
      setIsLoading(false);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError(null);
    setCopyFeedback('idle');
  };

  return (
    <div className="url-encoder">
      <h1>URL Encoder/Decoder</h1>
      <p>Encode URLs for safe transmission or decode encoded URLs back to readable format</p>

      <div className="encoder-container">
        <div className="form-group">
          <label htmlFor="mode">Operation Mode</label>
          <select
            id="mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
            data-testid="mode-select"
          >
            <option value="encode">Encode URL</option>
            <option value="decode">Decode URL</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="space-handling">Space Handling</label>
          <select
            id="space-handling"
            value={spaceHandling}
            onChange={(e) => setSpaceHandling(e.target.value as UrlSpaceHandling)}
            data-testid="space-handling-select"
          >
            <option value={UrlSpaceHandling.Encode}>Encode Spaces (%20)</option>
            <option value={UrlSpaceHandling.Plus}>Use Plus (+)</option>
            <option value={UrlSpaceHandling.Preserve}>Preserve Spaces</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="case-handling">Case Handling</label>
          <select
            id="case-handling"
            value={caseHandling}
            onChange={(e) => setCaseHandling(e.target.value as UrlCaseHandling)}
            data-testid="case-handling-select"
          >
            <option value={UrlCaseHandling.Preserve}>Preserve Case</option>
            <option value={UrlCaseHandling.Uppercase}>Convert to Uppercase</option>
            <option value={UrlCaseHandling.Lowercase}>Convert to Lowercase</option>
          </select>
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={partialEncoding}
              onChange={(e) => setPartialEncoding(e.target.checked)}
              data-testid="partial-encoding-checkbox"
            />
            Partial Encoding (only encode unsafe characters)
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="input-text">
            {mode === 'encode' ? 'URL to Encode' : 'URL to Decode'}
          </label>
          <textarea
            id="input-text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              mode === 'encode'
                ? 'Enter URL to encode (e.g., https://example.com/path?query=value)'
                : 'Enter encoded URL to decode (e.g., https%3A//example.com/path%3Fquery%3Dvalue)'
            }
            className="input-textarea"
            rows={6}
            data-testid="input-text"
          />
        </div>

        {error && (
          <div className="error-message" data-testid="error-message">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="loading-indicator" data-testid="loading-indicator">
            Processing...
          </div>
        )}

        <div className="form-group">
          <label htmlFor="output-text">
            {mode === 'encode' ? 'Encoded URL' : 'Decoded URL'}
          </label>
          <textarea
            id="output-text"
            value={output}
            readOnly
            placeholder="Result will appear here..."
            className="output-textarea"
            rows={6}
            data-testid="output-text"
          />
        </div>

        <div className="button-container">
          <button
            ref={copyButtonRef}
            onClick={handleCopy}
            disabled={!output}
            className={getButtonClass()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>

          {input.includes('\n') && (
            <button
              onClick={handleBatchProcess}
              disabled={isLoading}
              className="batch-button"
              data-testid="batch-button"
            >
              {isLoading ? 'Processing...' : 'Process Multiple URLs'}
            </button>
          )}

          <button
            onClick={clearAll}
            className="clear-button"
            data-testid="clear-button"
          >
            Clear All
          </button>
        </div>

        <div className="reference-section">
          <h3>URL Encoding Reference</h3>
          <div className="reference-table">
            <table>
              <thead>
                <tr>
                  <th>Character</th>
                  <th>Encoded</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Space</td>
                  <td>%20 or +</td>
                  <td>Space character</td>
                </tr>
                <tr>
                  <td>!</td>
                  <td>%21</td>
                  <td>Exclamation mark</td>
                </tr>
                <tr>
                  <td>#</td>
                  <td>%23</td>
                  <td>Hash/fragment</td>
                </tr>
                <tr>
                  <td>$</td>
                  <td>%24</td>
                  <td>Dollar sign</td>
                </tr>
                <tr>
                  <td>&amp;</td>
                  <td>%26</td>
                  <td>Ampersand</td>
                </tr>
                <tr>
                  <td>+</td>
                  <td>%2B</td>
                  <td>Plus sign</td>
                </tr>
                <tr>
                  <td>/</td>
                  <td>%2F</td>
                  <td>Forward slash</td>
                </tr>
                <tr>
                  <td>?</td>
                  <td>%3F</td>
                  <td>Question mark</td>
                </tr>
                <tr>
                  <td>=</td>
                  <td>%3D</td>
                  <td>Equals sign</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* No-JS Fallback */}
      <noscript>
        <div className="fallback-form">
          <form method="POST" action="/url-encoder/fallback" aria-label="URL Encoder Fallback Form">
            <div className="form-group">
              <label htmlFor="fallback-mode">Mode:</label>
              <select name="mode" id="fallback-mode" aria-label="Select encoding mode">
                <option value="encode">Encode</option>
                <option value="decode">Decode</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="fallback-input">Input:</label>
              <textarea name="input" id="fallback-input" rows={6} required aria-label="Input URL" />
            </div>
            <button type="submit" aria-label="Process URL">Process</button>
          </form>
        </div>
      </noscript>
    </div>
  );
};